import { createSlice } from '@reduxjs/toolkit';

/** Everything a signed-in session leaves in storage. */
const SESSION_KEYS = ['authToken', 'refreshToken', 'userId', 'emailVerified', 'isEmployee', 'userType'];

function clearStoredSession() {
  SESSION_KEYS.forEach((k) => {
    try { localStorage.removeItem(k); } catch { /* private mode */ }
  });
}

const initialState = {
  token: localStorage.getItem('authToken') || null,
  refreshToken: localStorage.getItem('refreshToken') || null,
  userId: localStorage.getItem('userId') || null,
  isLoggedIn: !!localStorage.getItem('authToken'),
  /* True only after signing out in this tab. Tells the route guard to send
     someone to the marketing page rather than the sign-in form: they just
     left, they are not trying to get in. Never persisted — a fresh load is
     someone arriving, so a deep link goes to the form. */
  signedOut: false,
  emailVerified: localStorage.getItem('emailVerified') === 'true',
  isEmployee: localStorage.getItem('isEmployee') === 'true',
  userType: localStorage.getItem('userType') || null,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart(state) {
      state.loading = true;
      state.error = null;
    },
    loginSuccess(state, action) {
      state.token = action.payload.authToken || action.payload;
      state.isLoggedIn = true;
      state.signedOut = false;
      state.loading = false;
      
      // Store additional data if provided
      if (typeof action.payload === 'object') {
        state.refreshToken = action.payload.refreshToken;
        state.userId = action.payload.userId;
        state.emailVerified = action.payload.emailVerified;
        state.isEmployee = !!action.payload.isEmployee;
        state.userType = action.payload.userType || null;

        // Also save to localStorage
        localStorage.setItem('authToken', action.payload.authToken);
        if (action.payload.refreshToken) localStorage.setItem('refreshToken', action.payload.refreshToken);
        if (action.payload.userId) localStorage.setItem('userId', action.payload.userId);
        if (action.payload.emailVerified !== undefined) localStorage.setItem('emailVerified', action.payload.emailVerified);
        localStorage.setItem('isEmployee', action.payload.isEmployee ? 'true' : 'false');
        if (action.payload.userType) localStorage.setItem('userType', action.payload.userType);
      } else {
        // Backward compatibility for when only token is passed
        localStorage.setItem('authToken', action.payload);
      }
    },
    loginFailure(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    logout(state) {
      state.token = null;
      state.refreshToken = null;
      state.userId = null;
      state.emailVerified = false;
      state.isEmployee = false;
      state.userType = null;
      state.isLoggedIn = false;
      state.signedOut = true;
      clearStoredSession();
    },
    /**
     * The session stopped being valid — the token expired, was revoked, or the
     * server no longer accepts it.
     *
     * Deliberately NOT `logout`: that sets `signedOut`, which means "this
     * person chose to leave" and sends them to the marketing page. Someone
     * whose session died mid-task wants the sign-in form and an explanation,
     * so `signedOut` stays false and the reason is carried in `error` for the
     * login screen to show.
     */
    sessionExpired(state, action) {
      state.token = null;
      state.refreshToken = null;
      state.userId = null;
      state.emailVerified = false;
      state.isEmployee = false;
      state.userType = null;
      state.isLoggedIn = false;
      state.signedOut = false;
      state.error = action.payload || 'Your session has ended. Please sign in again.';
      clearStoredSession();
    },

    clearError(state) {
      state.error = null;
    },
  },
});

export const {
  loginStart, loginSuccess, loginFailure, logout, sessionExpired, clearError,
} = authSlice.actions;
export default authSlice.reducer;
